import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RootLayout } from '@/components/layout/RootLayout'
import { GlassCard } from '@/components/ui/GlassCard'
import { GlassButton } from '@/components/ui/GlassButton'
import { GlassInput } from '@/components/ui/GlassInput'
import { api } from '@/lib/api'
import { hostingConnectionsQueryOptions, opsSecretsQueryOptions } from '@/lib/queryOptions'
import { useT } from '@/i18n/provider'

export const Route = createFileRoute('/settings/ops/hosting')({
  loader: async ({ context: { queryClient } }) => {
    await Promise.all([
      queryClient.ensureQueryData(hostingConnectionsQueryOptions()),
      queryClient.ensureQueryData(opsSecretsQueryOptions()),
    ])
  },
  component: OpsHostingPage,
})

function OpsHostingPage() {
  const t = useT()
  const qc = useQueryClient()
  const { data: connections = [] } = useQuery(hostingConnectionsQueryOptions())
  const { data: secrets = [] } = useQuery(opsSecretsQueryOptions())
  const [name, setName] = useState('')
  const [externalProjectId, setExternalProjectId] = useState('')
  const [externalEnvironmentId, setExternalEnvironmentId] = useState('')
  const [credentialSecretId, setCredentialSecretId] = useState('')

  const createMutation = useMutation({
    mutationFn: api.ops.hostingConnections.create,
    onSuccess: () => {
      setName('')
      setExternalProjectId('')
      setExternalEnvironmentId('')
      setCredentialSecretId('')
      qc.invalidateQueries({ queryKey: ['ops', 'hosting-connections'] })
    },
  })

  const syncMutation = useMutation({
    mutationFn: api.ops.hostingConnections.sync,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ops', 'hosting-connections'] }),
  })

  return (
    <RootLayout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-xl font-bold text-white">{t('settings.opsHosting.title')}</h1>
          <p className="text-sm text-white/40 mt-0.5">{t('settings.opsHosting.subtitle')}</p>
        </div>

        <GlassCard className="space-y-4">
          <h2 className="font-semibold text-white text-sm">{t('settings.opsHosting.createTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GlassInput
              label={t('settings.opsHosting.nameLabel')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('settings.opsHosting.namePlaceholder')}
            />
            <GlassInput
              label={t('settings.opsHosting.projectLabel')}
              value={externalProjectId}
              onChange={(e) => setExternalProjectId(e.target.value)}
              placeholder={t('settings.opsHosting.projectPlaceholder')}
            />
            <GlassInput
              label={t('settings.opsHosting.environmentLabel')}
              value={externalEnvironmentId}
              onChange={(e) => setExternalEnvironmentId(e.target.value)}
              placeholder={t('settings.opsHosting.environmentPlaceholder')}
            />
            <div>
              <label className="text-sm font-medium text-white/70 block mb-1.5">{t('settings.opsHosting.secretLabel')}</label>
              <select
                value={credentialSecretId}
                onChange={(e) => setCredentialSecretId(e.target.value)}
                className="glass w-full px-4 py-2.5 text-white text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
              >
                <option value="">{t('settings.opsHosting.secretPlaceholder')}</option>
                {secrets.map((secret) => (
                  <option key={secret.id} value={secret.id} style={{ background: '#1a1a2e' }}>
                    {secret.name} ({secret.provider})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <GlassButton
            onClick={() => createMutation.mutate({
              name,
              connectorType: 'RAILWAY',
              externalProjectId,
              externalEnvironmentId,
              credentialSecretId: credentialSecretId || undefined,
            })}
            loading={createMutation.isPending}
            disabled={!name || !externalProjectId}
          >
            {t('settings.opsHosting.createButton')}
          </GlassButton>
        </GlassCard>

        <div className="space-y-3">
          {connections.map((item) => (
            <GlassCard key={item.id} size="sm" className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-white">{item.name}</p>
                <p className="text-xs text-white/40">
                  {item.connectorType} · {item.status} · {item.externalProjectId ?? '—'}
                </p>
              </div>
              <GlassButton
                size="sm"
                variant="secondary"
                onClick={() => syncMutation.mutate(item.id)}
                loading={syncMutation.isPending}
              >
                {t('settings.opsHosting.syncButton')}
              </GlassButton>
            </GlassCard>
          ))}
        </div>
      </div>
    </RootLayout>
  )
}
